import React, { useState } from 'react';
import { CitationAnchor } from '../types/api';

interface CitationHighlightProps {
  answer: string;
  citations: CitationAnchor[];
  onCitationClick?: (citation: CitationAnchor) => void;
}

/**
 * Component that renders answer text with inline citation highlights and markdown formatting.
 * Parses [#anchor_text#] patterns and replaces them with clickable highlighted spans.
 * Also parses markdown formatting (bold, italic, lists, etc.)
 */
export const CitationHighlight: React.FC<CitationHighlightProps> = ({
  answer,
  citations,
  onCitationClick
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const parseMarkdown = (text: string): React.ReactNode => {
    // Split by newlines to handle line-by-line
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let key = 0;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      
      // Handle bullet lists: * text or - text
      if (/^\s*[*-]\s+/.test(line)) {
        const listItems: React.ReactNode[] = [];
        let listKey = 0;
        
        // Collect consecutive bullet items
        while (i < lines.length && /^\s*[*-]\s+/.test(lines[i])) {
          const content = lines[i].replace(/^\s*[*-]\s+/, '');
          listItems.push(
            <li key={listKey++} className="mb-1">
              {parseInlineMarkdown(content)}
            </li>
          );
          i++;
        }
        
        elements.push(
          <ul key={key++} className="list-disc ml-6 my-2">
            {listItems}
          </ul>
        );
        continue;
      }
      // Handle numbered lists: 1. text
      else if (/^\s*\d+\.\s+/.test(line)) {
        const listItems: React.ReactNode[] = [];
        let listKey = 0;
        
        // Collect consecutive numbered items
        while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
          const content = lines[i].replace(/^\s*\d+\.\s+/, '');
          listItems.push(
            <li key={listKey++} className="mb-1">
              {parseInlineMarkdown(content)}
            </li>
          );
          i++;
        }
        
        elements.push(
          <ol key={key++} className="list-decimal ml-6 my-2">
            {listItems}
          </ol>
        );
        continue;
      }
      // Handle empty lines
      else if (line.trim() === '') {
        elements.push(<br key={key++} />);
      }
      // Regular paragraph
      else {
        elements.push(
          <span key={key++}>
            {parseInlineMarkdown(line)}
            {i < lines.length - 1 && <br />}
          </span>
        );
      }
      
      i++;
    }

    return elements;
  };

  const parseInlineMarkdown = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Check for bold: **text**
      const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
      if (boldMatch) {
        parts.push(
          <strong key={key++}>
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Check for italic: *text* (but not **)
      const italicMatch = remaining.match(/^\*([^*]+?)\*/);
      if (italicMatch) {
        parts.push(
          <em key={key++}>
            {italicMatch[1]}
          </em>
        );
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      // Check for code: `text`
      const codeMatch = remaining.match(/^`(.+?)`/);
      if (codeMatch) {
        parts.push(
          <code key={key++} className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }

      // No markdown found, add next character
      parts.push(remaining[0]);
      remaining = remaining.slice(1);
    }

    return parts;
  };

  const renderAnswerWithCitations = () => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = /\[#(.+?)#\]/g;
    let match;
    let citationIndex = 0;

    while ((match = regex.exec(answer)) !== null) {
      // Add text before citation (with markdown parsing)
      if (match.index > lastIndex) {
        const textBefore = answer.substring(lastIndex, match.index);
        parts.push(
          <span key={`text-${lastIndex}`}>
            {parseMarkdown(textBefore)}
          </span>
        );
      }

      // Add citation as highlighted clickable text
      const citation = citations[citationIndex];
      const anchorText = match[1];

      parts.push(
        <span
          key={`citation-${citationIndex}`}
          className={`citation-highlight ${hoveredIndex === citationIndex ? 'hovered' : ''}`}
          onClick={() => citation && onCitationClick?.(citation)}
          onMouseEnter={() => setHoveredIndex(citationIndex)}
          onMouseLeave={() => setHoveredIndex(null)}
          title={citation ? `Click to view source (confidence: ${Math.round(citation.matchConfidence * 100)}%)` : 'Citation not resolved'}
          style={{
            backgroundColor: citation ? '#fff3cd' : '#f8d7da',
            borderBottom: `2px solid ${citation ? '#ffc107' : '#dc3545'}`,
            cursor: citation ? 'pointer' : 'default',
            padding: '2px 4px',
            borderRadius: '3px',
            transition: 'background-color 0.2s',
          }}
        >
          {anchorText}
        </span>
      );

      citationIndex++;
      lastIndex = regex.lastIndex;
    }

    // Add remaining text (with markdown parsing)
    if (lastIndex < answer.length) {
      const textAfter = answer.substring(lastIndex);
      parts.push(
        <span key={`text-${lastIndex}`}>
          {parseMarkdown(textAfter)}
        </span>
      );
    }

    return parts;
  };

  return (
    <div data-component="CitationHighlight" className="citation-answer prose prose-sm max-w-none">
      {renderAnswerWithCitations()}
    </div>
  );
};
